import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useInitLoginApiAuthInitPost } from '@/shared/api/generated/auth/auth';
import {
  getGetMeApiUsersMeGetQueryKey,
  getMeApiUsersMeGet,
} from '@/shared/api/generated/users/users';
import { useOAuthDialogStore } from '@/shared/store';
import { setUser } from '@/shared/store/auth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';

/**
 * OAuth 연결 다이얼로그 컴포넌트
 *
 * 에이전트 실행 시 특정 OAuth 연결이 필요한 경우 표시됩니다.
 * 네이버 로그인과 동일한 팝업 방식을 사용합니다.
 *
 * 플로우:
 * 1. 다이얼로그에서 "연결" 버튼 클릭
 * 2. POST /api/auth/init 호출 → auth_url 획득
 * 3. 팝업 창에서 OAuth 로그인 진행
 * 4. 콜백 완료 → postMessage로 성공 알림
 * 5. 페이지 새로고침 없이 세션 갱신
 */
export function OAuthDialog() {
  const { isOpen, provider, title, description, closeDialog } = useOAuthDialogStore();
  const { mutate: initLogin, isPending } = useInitLoginApiAuthInitPost();
  const queryClient = useQueryClient();

  // 팝업 상태 관리
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const isProcessingRef = useRef(false);
  // 팝업 열릴 때의 provider 저장 (콜백에서 사용)
  const providerRef = useRef<'google' | 'naver' | null>(null);

  // 로그인 성공 처리 (중복 실행 방지)
  const handleLoginSuccess = useCallback(async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsPopupOpen(false);

    try {
      const queryKey = getGetMeApiUsersMeGetQueryKey();
      const meResponse = await getMeApiUsersMeGet();

      // Zustand 상태 업데이트
      if (meResponse?.authenticated && meResponse.user) {
        setUser({
          id: meResponse.user.id,
          email: meResponse.user.email,
          name: meResponse.user.name ?? null,
          role: meResponse.user.role ?? null,
        });
      }

      // React Query 캐시 업데이트
      queryClient.setQueryData(queryKey, meResponse);
      queryClient.invalidateQueries({ queryKey });

      // ref에서 provider 가져오기 (store는 이미 초기화되었을 수 있음)
      const connectedProvider = providerRef.current;
      const providerLabel =
        connectedProvider === 'google'
          ? 'Google'
          : connectedProvider === 'naver'
            ? '네이버'
            : '계정';
      toast.success(`${providerLabel}이(가) 연결되었습니다`);
      closeDialog();
    } catch (error) {
      toast.error('계정 연결 처리 중 오류가 발생했습니다');
      console.error('OAuth success handler error:', error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [queryClient, closeDialog]);

  // postMessage 이벤트 리스너 (주 메커니즘)
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'LOGIN_SUCCESS') {
        await handleLoginSuccess();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleLoginSuccess]);

  // 서버 세션 polling (fallback)
  useEffect(() => {
    if (!isPopupOpen) return;

    const pollInterval = setInterval(async () => {
      try {
        if (popupRef.current?.closed) {
          const data = await queryClient.fetchQuery({
            queryKey: getGetMeApiUsersMeGetQueryKey(),
            staleTime: 0,
          });

          if ((data as { authenticated?: boolean })?.authenticated) {
            await handleLoginSuccess();
          }
        }
      } catch (error) {
        console.debug('Session check failed:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isPopupOpen, queryClient, handleLoginSuccess]);

  const handleConnect = () => {
    if (!provider) return;

    // provider 값을 ref에 저장 (콜백에서 사용)
    providerRef.current = provider;

    initLogin(
      { data: { provider } },
      {
        onSuccess: ({ auth_url }) => {
          // 팝업 창 크기 및 중앙 위치 계산
          const width = 600;
          const height = 700;
          const left = Math.round((window.screen.width - width) / 2);
          const top = Math.round((window.screen.height - height) / 2);

          const popup = window.open(
            auth_url,
            `${provider}-oauth`,
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
          );

          if (!popup) {
            toast.error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
          } else {
            popupRef.current = popup;
            setIsPopupOpen(true);
          }
        },
        onError: () => {
          toast.error('계정 연결 시작에 실패했습니다');
        },
      },
    );
  };

  const providerLabel = provider === 'google' ? 'Google' : '네이버';

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending || isPopupOpen}>취소</AlertDialogCancel>
          <AlertDialogAction onClick={handleConnect} disabled={isPending || isPopupOpen}>
            {isPending || isPopupOpen ? '연결 중...' : `${providerLabel} 연결`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
